'use client';

import { useTranslations } from 'next-intl';
import { type ReactNode, useMemo } from 'react';
import { AgentLineConfiguration } from '@/app/components/icons/AgentLineConfiguration';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { SettingRadioGroup } from '@/app/components/shared/SettingRadioGroup';
import { Button } from '@/app/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { AgentSceneConfig, AgentSettings, AgentVoiceOption } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { getAspectRatioLabel, getAspectRatioOptions, getResolutionLabel, getResolutionOptions } from '../../_utils';
import { ChooseAudioDialog } from './ChooseAudioDialog';

interface ChatSettingsProps {
  config: AgentSceneConfig['settingsConfig'];
  selectedSettings: AgentSettings;
  selectedVoice: AgentVoiceOption;
  onUpdateSelectedVoice: (selectedVoice: AgentVoiceOption) => void;
  onUpdateSetting: <K extends keyof AgentSettings>(key: K, value: AgentSettings[K]) => void;
  showValue?: boolean;
}

/**
 * 设置项配置类型
 */
interface SettingItemConfig {
  key: keyof AgentSettings;
  titleKey:
    | 'create.createForm.settings.duration.title'
    | 'create.createForm.settings.resolution.title'
    | 'create.createForm.settings.aspectRatio.title';
  value: AgentSettings[keyof AgentSettings] | undefined;
  options: Array<{ value: string; label: string; badgeEle?: ReactNode }>;
  onValueChange: (value: string) => void;
}

/**
 * 设置项组件 Props
 */
interface SettingItemProps {
  title: string;
  value: string | undefined;
  options: Array<{ value: string; label: string }>;
  onValueChange: (value: string) => void;
}

/**
 * 单个设置项组件
 */
function SettingItem({ title, value, options, onValueChange }: SettingItemProps) {
  return (
    <>
      <div className="px-2 py-1 text-system-white/40 text-xs leading-[1.4]">{title}</div>
      <SettingRadioGroup
        value={value}
        options={options}
        onValueChange={onValueChange}
        className="rounded-8 bg-system-black16"
        optionsWrapperClassName="hover:bg-system-white08 rounded-8"
        activeOptionClassName="border-system-white12 bg-system-white24"
      />
    </>
  );
}

/**
 * 聊天设置组件
 * 提供视频生成相关的设置选项（时长、分辨率、宽高比）
 */
export function ChatSettings({
  config,
  selectedSettings,
  onUpdateSetting,
  showValue,
  selectedVoice,
  onUpdateSelectedVoice,
}: ChatSettingsProps) {
  const t = useTranslations();
  const userInfo = useStore((state) => state.userInfo);
  const { agentReadOnly } = useStore((state) => ({
    agentReadOnly: state.agentReadOnly,
  }));
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  // 使用 useMemo 优化设置项配置的计算
  const settingItems = useMemo<SettingItemConfig[]>(() => {
    const items: SettingItemConfig[] = [];

    // Duration 设置项
    if (config?.duration && config.duration.length > 0) {
      items.push({
        key: 'duration',
        titleKey: 'create.createForm.settings.duration.title',
        value: selectedSettings.duration,
        options: config.duration.map((duration) => ({
          value: duration.toString(),
          label: `${duration}s`,
          badgeEle:
            !agentReadOnly && userInfo?.subs_plan === 'premium_v2' && Number(duration) === 30 ? (
              <div className="absolute top-0 right-0 rounded-tr-8 rounded-bl-8 bg-[linear-gradient(0deg,rgba(76,99,255,0.48)_0%,rgba(76,99,255,0.48)_100%)] px-2 py-0.5 text-[9px] leading-[1.6]">
                {t('agentProject.chatSettings.upgrade')}
              </div>
            ) : null,
        })),
        onValueChange: (value) => {
          if (!agentReadOnly && userInfo?.subs_plan === 'premium_v2' && Number(value) === 30) {
            openPlanDetailDialog({
              scene: PlanDialogScene.AgentProject,
              fromPage: 'chat_settings_upgrade',
            });
            reportEvent('vip_price_modal_exposure', {
              from_page: 'chat_settings_upgrade',
            });
            return;
          }
          onUpdateSetting('duration', Number(value));
        },
      });
    }

    // Resolution 设置项
    if (config?.resolution && config.resolution.length > 0) {
      items.push({
        key: 'resolution',
        titleKey: 'create.createForm.settings.resolution.title',
        value: selectedSettings.resolution,
        options: getResolutionOptions(config.resolution),
        onValueChange: (value) => onUpdateSetting('resolution', value as AgentSettings['resolution']),
      });
    }

    // AspectRatio 设置项
    if (config?.ratio && config.ratio.length > 0) {
      items.push({
        key: 'ratio',
        titleKey: 'create.createForm.settings.aspectRatio.title',
        value: selectedSettings.ratio,
        options: getAspectRatioOptions(config.ratio),
        onValueChange: (value) => onUpdateSetting('ratio', value as AgentSettings['ratio']),
      });
    }

    return items;
  }, [config, selectedSettings, onUpdateSetting]);

  // 格式化显示值
  const displayValues = useMemo(() => {
    const values: string[] = [];

    if (selectedSettings.duration) {
      values.push(`${selectedSettings.duration}s`);
    }
    if (selectedSettings.resolution) {
      values.push(getResolutionLabel(selectedSettings.resolution));
    }
    if (selectedSettings.ratio) {
      values.push(getAspectRatioLabel(selectedSettings.ratio));
    }

    return values;
  }, [selectedSettings]);

  return (
    <Popover>
      <CustomTooltip
        content={t('agentProject.dialogSettingsTip.settings')}
        side="top"
        sideOffset={4}
        disableHoverableContent={true}
        variant="glass"
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'group flex h-full items-center gap-1.5 rounded-full bg-system-white04 p-2 hover:border-system-white64 hover:bg-system-white24',
              showValue && 'min-w-[154px]',
            )}
          >
            <AgentLineConfiguration className="size-4 flex-shrink-0" />

            {showValue && displayValues.length > 0 && (
              <div className="flex flex-1 items-center gap-2 border-system-white08 border-l pr-1 pl-2 text-system-white48 text-xs group-hover:text-system-white">
                {displayValues.map((value) => (
                  <span key={value}>{value}</span>
                ))}
              </div>
            )}
          </Button>
        </PopoverTrigger>
      </CustomTooltip>
      <PopoverContent
        className="w-[300px] rounded-12 border border-system-white08 bg-[rgba(34,37,42,0.48)] p-1 shadow-lg backdrop-blur-[30px] lg:w-[300px]"
        sideOffset={8}
        align="center"
        side="top"
      >
        <div className="space-y-2">
          {settingItems.map((item) => (
            <SettingItem
              key={item.key}
              title={t(item.titleKey)}
              value={item.value?.toString()}
              options={item.options}
              onValueChange={item.onValueChange}
            />
          ))}

          <div className="px-2 py-1 text-system-white/40 text-xs leading-[1.4]">
            {t('agentProject.voiceSelect.title')}
          </div>
          <ChooseAudioDialog selectedAudio={selectedVoice} changeAudio={onUpdateSelectedVoice} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
